import { useCallback, useEffect } from "react";
import { useAppDispatch } from "../../../services/redux/hooks/use-dispatch";
import { useAppSelector } from "../../../services/redux/hooks/use-selector";
import {
  selectMeetingValue,
  selectRecognizeHash,
  selectRecognizeMetaObject,
  selectSpeakers,
} from "../redux/selectors";
import { getAudioTranscriptionAction } from "../redux/actions";
import { useStorageSelectors } from "../../../app/state/storageObjectSlice/hook/use-storage-selectors";
import { updateRecognizeMeta } from "../redux/slice";
import { useAppModals } from "../../../app/state/modalSlice/hook/use-app-modals";

export const useAudioRecognize = () => {
  const dispatch = useAppDispatch();
  const hash = useAppSelector(selectRecognizeHash);
  const { selectedId } = useStorageSelectors("meetings");
  const value = useAppSelector(selectMeetingValue);
  const speakers = useAppSelector(selectSpeakers);
  const recognizeMeta = useAppSelector((state) =>
    selectRecognizeMetaObject(state, selectedId as string),
  );
  const { handleOpenModal } = useAppModals();

  const startRecognitionWithCheckSpeakers = useCallback(() => {
    if (!selectedId) return;
    if (speakers.length > 0) {
      handleOpenModal("warning_modal", null);
      return;
    } else {
      dispatch(
        updateRecognizeMeta(selectedId, {
          is_recognize: true,
        }),
      );
    }
  }, [selectedId, speakers.length, dispatch]);

  const startRecognition = useCallback(() => {
    if (!selectedId) return;

    dispatch(
      updateRecognizeMeta(selectedId, {
        is_recognize: true,
      }),
    );
  }, [selectedId, speakers, dispatch]);

  useEffect(() => {
    if (!selectedId || !hash) {
      return;
    }

    const shouldStartPolling = () => {
      return (
        value?.meetings_object?.meta?.status === "processing" ||
        (recognizeMeta && recognizeMeta.is_recognize === true)
      );
    };

    if (!shouldStartPolling()) {
      return;
    }

    let isPolling = true;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const poll = async () => {
      if (!isPolling) return;

      try {
        const newValue = await dispatch(
          getAudioTranscriptionAction(hash),
        ).unwrap();
        const status = newValue?.meetings_object?.meta?.status;

        if (status === "processing") {
          if (isPolling) {
            timeoutId = setTimeout(poll, 3000);
          }
        } else if (status === "completed" || status === "failed") {
          isPolling = false;
        }
      } catch (error) {
        console.warn("Audio recognition polling error:", error);
        isPolling = false;
      }
    };

    poll();

    return () => {
      isPolling = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [
    selectedId,
    hash,
    value?.meetings_object?.meta?.status,
    recognizeMeta,
    dispatch,
  ]);

  return {
    recognizeMeta,
    startRecognitionWithCheckSpeakers,
    startRecognition,
  };
};
